import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { useAuthStore } from '@/views/auth/auth.store';

export interface ApiResponse<T> {
  error: { message: string; status?: number } | null;
  isSuccess: boolean;
  data: T | null;
  /**
   * True iff the server returned `204 No Content`. Mutually exclusive
   * with `error`: 204 is still a success (`isSuccess === true`) — it
   * signals the request was understood but there is no body to return.
   *
   * Analytics endpoints (climate / LULC / Hansen / ARR potential) use
   * 204 to distinguish "valid query, no data for this polygon" from
   * either "error" or "200 with empty payload". Downstream consumers
   * should branch on this flag to surface a "no data available" state
   * instead of an error or a spinning loader.
   *
   * Use this flag instead of `!data` heuristics — a 200 OK with `null`
   * body is still distinct from a 204, and conflating them masks real
   * errors as empty data.
   *
   * Optional so the many manual `ApiResponse` constructions across the
   * codebase (services that wrap a third-party fetch, error envelopes
   * built without going through `ApiService.request`) keep compiling
   * without a noisy migration. Treat missing as `false`.
   */
  isNoContent?: boolean;
}

/**
 * The array a listing route declares, or null when the 2xx body is not one.
 *
 * `request` hands back whatever axios parsed, so an empty or non-JSON 200
 * arrives as a string. Indexing a string yields a character and `.find` on it
 * throws, which took the home project card down once in prod (Sentry
 * TYPESCRIPT-VUE-2K, 2026-09-21; not reproducible afterwards across all 63 prod
 * projects). Null lets the caller take its existing missing-response path. The
 * `console.error` keeps the malformed body visible in Sentry: the message is
 * fixed per route so events group, and the body snippet rides as an argument.
 *
 * An empty listing is a 204, which `request` already turns into `null` (checked
 * on prod 2026-09-24 for `/zones/{id}/layers`, notes and disturbances), so an
 * empty-string 200 is malformed too and is logged like any other.
 */
export const arrayBodyOrNull = <T>(res: ApiResponse<T[]> | null, route: string): T[] | null => {
  const body: unknown = res?.data;
  if (body == null) {
    return null;
  }
  if (Array.isArray(body)) {
    return body as T[];
  }
  console.error(`[api] ${route} answered 2xx with a non-array body; treating it as missing.`, {
    type: typeof body,
    body: (typeof body === 'string' ? body : JSON.stringify(body)).slice(0, 200),
  });
  return null;
};

/**
 * `url` as a stable log label: origin, query and numeric path ids dropped, so
 * `/zones/1732/layers` and `/zones/1292/layers` group as `/zones/{id}/layers`.
 */
const routeLabel = (url: string): string => {
  const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split('?')[0];
  return path.replace(/\/\d+(?=\/|$)/g, '/{id}') || url;
};

export class ApiService {
  public readonly authStore: ReturnType<typeof useAuthStore>;

  constructor(authStore: ReturnType<typeof useAuthStore>) {
    this.authStore = authStore;
  }

  /**
   * GET a listing route. `data` is the array, or null for a 204 or for a 2xx
   * body that is not an array (see `arrayBodyOrNull`). Use this, not
   * `get<T[]>`, for anything the caller will index, `.find` or `.map`.
   */
  public async getList<T>(url: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T[]>> {
    const res = await this.get<T[]>(url, config);
    return { ...res, data: arrayBodyOrNull(res, routeLabel(url)) };
  }

  public async get<T>(url: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('get', url, undefined, config);
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>('post', url, data, config);
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.request<T>('patch', url, data, config);
  }

  public async put<T>(url: string, data?: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('put', url, data, config);
  }

  public async delete<T>(url: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>('delete', url, undefined, config);
  }

  private async request<T>(
    method: 'get' | 'post' | 'patch' | 'put' | 'delete',
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<T>> {
    const token = await this.authStore.getCognitoToken();

    const defaultConfig: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      ...config,
    };

    try {
      let response: AxiosResponse<T>;

      switch (method) {
        case 'get':
          response = await axios.get<T>(url, defaultConfig);
          break;

        case 'post':
          response = await axios.post<T>(url, data, defaultConfig);
          break;

        case 'patch':
          response = await axios.patch<T>(url, data, defaultConfig);
          break;

        case 'put':
          response = await axios.put<T>(url, data, defaultConfig);
          break;

        case 'delete':
          response = await axios.delete<T>(url, defaultConfig);
          break;
      }

      const isNoContent = response.status === 204;
      return {
        error: null,
        isSuccess: true,
        // Axios returns `''` or `undefined` for a 204 body; normalise to
        // `null` so callers don't have to disambiguate the two shapes.
        data: isNoContent ? null : response.data,
        isNoContent,
      };
    } catch (error) {
      return { error: this.handleError(error), isSuccess: false, data: null };
    }
  }

  public handleError(error: any): { message: string; status?: number } {
    console.log({ error });
    if (axios.isAxiosError(error)) {
      const message =
        error.response?.data?.message ?? error.message ?? 'An error occurred during the request';

      return { message, status: error.response?.status ?? error.status };
    }

    return { message: 'Unknown error', status: error?.status ?? error?.statusCode };
  }
}
